/**
 * Health Status Calculator for Tekkr Metrics
 * Determines health status (green/yellow/red) based on current value vs. target
 */

export type HealthStatus = 'green' | 'yellow' | 'red';

export interface HealthThresholds {
  target: number;
  isHigherBetter: boolean; // true for metrics like compliance %, false for metrics like backlog count
}

/**
 * Calculate health status based on current value and target
 * 
 * For "higher is better" metrics (e.g., compliance %, SLA %):
 * - Green: >= target
 * - Yellow: >= 80% of target
 * - Red: < 80% of target
 * 
 * For "lower is better" metrics (e.g., overdue backlog, response time):
 * - Green: <= target
 * - Yellow: <= 150% of target
 * - Red: > 150% of target
 */
export function calculateHealthStatus(
  current: number,
  target: number,
  isHigherBetter: boolean = true
): HealthStatus {
  if (isHigherBetter) {
    // For metrics like compliance % or SLA %
    if (current >= target) {
      return 'green';
    } else if (current >= target * 0.8) {
      return 'yellow';
    } else {
      return 'red';
    }
  } else {
    // For metrics like backlog count or response time
    if (current <= target) {
      return 'green';
    } else if (current <= target * 1.5) {
      return 'yellow';
    } else {
      return 'red';
    }
  }
}

/**
 * Calculate trend direction from a series of values
 * Returns 'up' if trending upward, 'down' if trending downward, 'flat' if stable
 */
export function calculateTrendDirection(values: number[]): 'up' | 'down' | 'flat' {
  if (values.length < 2) {
    return 'flat';
  }
  
  const firstHalf = values.slice(0, Math.floor(values.length / 2));
  const secondHalf = values.slice(Math.floor(values.length / 2));
  
  const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;
  
  const percentChange = ((secondAvg - firstAvg) / firstAvg) * 100;
  
  if (percentChange > 2) {
    return 'up';
  } else if (percentChange < -2) {
    return 'down';
  } else {
    return 'flat';
  }
}

/**
 * Calculate team health based on number of metrics in each status
 * A team is "at risk" if it has 2+ red metrics
 */
export function calculateTeamHealth(
  channelCompliance: number,
  slaAdherence: number
): HealthStatus {
  const complianceHealth = calculateHealthStatus(channelCompliance, 85, true);
  const slaHealth = calculateHealthStatus(slaAdherence, 90, true);
  
  // Count red metrics
  const redCount = [complianceHealth, slaHealth].filter(h => h === 'red').length;
  
  if (redCount >= 2) {
    return 'red';
  } else if (redCount === 1 || complianceHealth === 'yellow' || slaHealth === 'yellow') {
    return 'yellow';
  } else {
    return 'green';
  }
}

/**
 * Format metric value for display
 */
export function formatMetricValue(value: number, unit: string): string {
  if (unit === '%') {
    return `${value}${unit}`;
  } else if (unit === 'hours') {
    return `${value}h`;
  } else if (unit === 'tickets') {
    return `${value}`;
  } else {
    return `${value} ${unit}`;
  }
}

